import React, { useEffect } from 'react';
import { Play, Pause, X, Maximize2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useHabitSessionStore } from '../stores/habitSessionStore';
import { formatPoints } from '../utils/levelCalculation';

interface HabitSessionWidgetProps {
  onExpand: () => void;
  onStop: () => void;
}

export function HabitSessionWidget({ onExpand, onStop }: HabitSessionWidgetProps) {
  const { activeSession, pauseSession, resumeSession, calculateSessionXP, updateElapsedTime } = useHabitSessionStore();
  const { getCategoryColor } = useCategories();

  useEffect(() => {
    if (!activeSession || activeSession.pausedAt) return;

    const interval = setInterval(() => {
      updateElapsedTime();
    }, 1000);

    return () => clearInterval(interval);
  }, [activeSession?.pausedAt]);

  if (!activeSession) return null;

  const categoryColor = getCategoryColor(activeSession.habitCategory);
  const HabitIcon = activeSession.habitIcon && (Icons[activeSession.habitIcon as keyof typeof Icons] as any)
    ? (Icons[activeSession.habitIcon as keyof typeof Icons] as any)
    : Icons.Star;

  const isPaused = !!activeSession.pausedAt;
  const currentXP = calculateSessionXP(activeSession.elapsedSeconds);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTogglePause = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPaused) {
      resumeSession();
    } else {
      pauseSession();
    }
  };

  const handleStop = (e: React.MouseEvent) => {
    e.stopPropagation();
    onStop();
  };

  const timeLabel =
    activeSession.sessionType === 'timer' && activeSession.timerDurationSeconds
      ? formatTime(Math.max(0, activeSession.timerDurationSeconds - activeSession.elapsedSeconds))
      : formatTime(activeSession.elapsedSeconds);

  return (
    <div
      className="flex w-fit max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 cursor-pointer hover:brightness-110 transition-all animate-pulse-slow"
      style={{
        backgroundColor: `${categoryColor}14`,
        borderColor: categoryColor
      }}
      onClick={onExpand}
    >
      <span
        className="flex items-center justify-center rounded-full p-1 shrink-0"
        style={{ backgroundColor: `${categoryColor}26` }}
      >
        <HabitIcon className="w-3 h-3" style={{ color: categoryColor }} />
      </span>

      <span className="text-xs font-semibold text-foreground truncate max-w-[96px]">
        {activeSession.habitName}
      </span>
      <span className="text-muted-foreground text-xs">·</span>
      <span className="font-mono text-xs text-foreground tabular-nums whitespace-nowrap">
        {timeLabel}
      </span>
      <span className="text-muted-foreground text-xs">·</span>
      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: categoryColor }}>
        {formatPoints(currentXP)} XP
      </span>

      <button
        onClick={handleTogglePause}
        className="ml-0.5 p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title={isPaused ? 'Resume' : 'Pause'}
      >
        {isPaused ? (
          <Play className="w-3.5 h-3.5" style={{ color: categoryColor }} />
        ) : (
          <Pause className="w-3.5 h-3.5" style={{ color: categoryColor }} />
        )}
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onExpand();
        }}
        className="hidden sm:flex p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="Expand"
      >
        <Maximize2 className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      <button
        onClick={handleStop}
        className="hidden sm:flex p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="Stop Session"
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
